"use client";

import Image from "next/image";
import { Finn } from "@/app/components/Finn";
import Link from "@/app/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import { BlogPost } from "@/app/lib/blog-utils";
import Button from "@/app/components/Button";
import HomeNavbar from "@/app/components/HomeNavbar";
import Footer from "@/app/components/Footer";
import { Fragment, useState, useCallback, useEffect } from "react";
import HowHeyFinnWorks from "../../components/HowHeyFinnWorks";
import RichText from "@/app/components/RichText";
import { isValidLocale, DEFAULT_LOCALE } from "@/app/lib/i18n-config";
import { loadTranslations } from "@/app/utils/i18next/load";

// Build a share URL with UTM parameters for attribution tracking
function getShareUrl(platform: string): string {
  const url = new URL(window.location.href);
  // Strip any existing UTM params to prevent stacking
  for (const key of [...url.searchParams.keys()]) {
    if (key.startsWith("utm_")) {
      url.searchParams.delete(key);
    }
  }
  url.searchParams.set("utm_source", platform);
  url.searchParams.set("utm_medium", "organic_social");
  url.searchParams.set("utm_campaign", "blog_post");
  // Extract slug from pathname (e.g. /blog/pillar/my-post → my-post)
  const pathSegments = url.pathname.split("/").filter(Boolean);
  const blogSlug =
    pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : "";
  if (blogSlug) url.searchParams.set("utm_content", blogSlug);
  return url.toString();
}

// Share buttons component
function ShareButtons({ title }: { title: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopyLink = useCallback(async () => {
    const shareUrl = getShareUrl("copy_link");
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);

  const shareOnLinkedIn = () => {
    const url = encodeURIComponent(getShareUrl("linkedin"));
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const shareOnTwitter = () => {
    const url = encodeURIComponent(getShareUrl("x"));
    const text = encodeURIComponent(title);
    window.open(
      `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(getShareUrl("facebook"));
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const shareOnWhatsApp = () => {
    const url = encodeURIComponent(getShareUrl("whatsapp"));
    const text = encodeURIComponent(title);
    window.open(
      `https://wa.me/?text=${text}%20${url}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-signal-muted mr-1">
        {t("blog.shareArticle")}:
      </span>
      <button
        onClick={shareOnLinkedIn}
        className="btn-glass-ghost rounded-full p-2 text-signal-muted hover:text-[#0077B5] transition-colors"
        aria-label="Share on LinkedIn"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      </button>
      <button
        onClick={shareOnFacebook}
        className="btn-glass-ghost rounded-full p-2 text-signal-muted hover:text-[#1877F2] transition-colors"
        aria-label="Share on Facebook"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      </button>
      <button
        onClick={shareOnWhatsApp}
        className="btn-glass-ghost rounded-full p-2 text-signal-muted hover:text-[#25D366] transition-colors"
        aria-label="Share on WhatsApp"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </button>
      <button
        onClick={shareOnTwitter}
        className="btn-glass-ghost rounded-full p-2 text-signal-muted hover:text-black transition-colors"
        aria-label="Share on X"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </button>
      <button
        onClick={handleCopyLink}
        className={`p-2 rounded-full transition-colors ${
          copied
            ? "bg-green-100 text-green-600"
            : "btn-glass-ghost text-signal-muted hover:text-signal-deep"
        }`}
        aria-label={t("blog.copyLink")}
      >
        {copied ? (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        )}
      </button>
      {copied && (
        <span className="text-xs text-signal-success animate-pulse">
          {t("blog.linkCopied")}
        </span>
      )}
    </div>
  );
}

function formatDate(dateString: string, locale: string = "de-CH"): string {
  const date = new Date(dateString);
  // Map i18n language codes to locale codes
  const localeMap: Record<string, string> = {
    de: "de-CH",
    en: "en-US",
    fr: "fr-CH",
    it: "it-CH",
    es: "es-ES",
  };
  const dateLocale = localeMap[locale] || locale;
  return new Intl.DateTimeFormat(dateLocale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

// RichText is imported from a shared component for reuse across blog pages

interface BlogClientProps {
  blogPost: BlogPost;
  pillar?: { slug: string; title: string };
  readingTime: number;
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function buildToc(contentBlocks: BlogPost["contentBlocks"]) {
  const includedTypes: Array<BlogPost["contentBlocks"][number]["type"]> = [
    "textSection",
    "bulletList",
    "comparisonSection",
  ];
  const idCounts = new Map<string, number>();
  const anchorIds: Array<string | null> = [];
  const tocItems: Array<{ id: string; title: string }> = [];

  contentBlocks.forEach(block => {
    if (
      includedTypes.includes(block.type) &&
      "title" in block &&
      typeof block.title === "string"
    ) {
      const base = slugify(block.title);
      const current = idCounts.get(base) ?? 0;
      const next = current + 1;
      idCounts.set(base, next);
      const id = current === 0 ? base : `${base}-${next}`;
      anchorIds.push(id);
      tocItems.push({ id, title: block.title as string });
    } else {
      anchorIds.push(null);
    }
  });

  return { anchorIds, tocItems };
}

export default function BlogClient({
  blogPost,
  pillar,
  readingTime,
}: BlogClientProps) {
  const { t, i18n } = useTranslation();
  const { anchorIds, tocItems } = buildToc(blogPost.contentBlocks);
  const firstCtaIndex = blogPost.contentBlocks.findIndex(
    b => b.type === "ctaSection"
  );

  // Sync i18n language to blog post's meta.lang
  // (<html lang> is set server-side by the root layout via getBlogPost)
  useEffect(() => {
    const blogLang = blogPost.meta.lang;
    if (blogLang && isValidLocale(blogLang) && i18n.language !== blogLang) {
      if (blogLang === DEFAULT_LOCALE) {
        i18n.changeLanguage(blogLang);
      } else {
        loadTranslations(blogLang).then(translation => {
          i18n.addResourceBundle(
            blogLang,
            "translation",
            translation,
            true,
            true
          );
          i18n.changeLanguage(blogLang);
        });
      }
    }
  }, [blogPost.meta.lang, i18n]);

  return (
    <div className="glass-page text-signal-ink">
      <HomeNavbar floating />
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Long-form reading surface: strong glass so prose stays crisp */}
        <div className="glass-strong rounded-glassPanel px-5 py-8 sm:px-10 sm:py-12">
          {/* Hero Section */}
          <section className="mb-12">
            {blogPost.heroSection.video ? (
              <div className="relative w-full rounded-glassInner overflow-hidden mb-8 bg-gray-900">
                <video
                  src={blogPost.heroSection.video}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-auto rounded-glassInner"
                  {...(blogPost.heroSection.image
                    ? { poster: blogPost.heroSection.image }
                    : {})}
                />
              </div>
            ) : blogPost.heroSection.image ? (
              <div className="relative w-full rounded-glassInner overflow-hidden mb-8 bg-gray-100 dark:bg-gray-800">
                <Image
                  src={blogPost.heroSection.image}
                  alt={blogPost.heroSection.headline}
                  width={1200}
                  height={630}
                  className="w-full h-auto"
                  priority
                />
              </div>
            ) : null}
            {/* Author, date, reading time and share row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-center gap-4 flex-wrap">
                {blogPost.author ? (
                  <div className="flex items-center gap-3">
                    <Image
                      src={blogPost.author.avatar}
                      alt={blogPost.author.name}
                      width={44}
                      height={44}
                      className="rounded-full border-2 border-white shadow-glassInset"
                    />
                    <div>
                      <p className="text-sm font-medium text-signal-ink">
                        {blogPost.author.name}
                      </p>
                      {blogPost.author.role && (
                        <p className="text-xs text-signal-muted">
                          {blogPost.author.role}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Finn className="w-12 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-signal-ink">
                        Finn
                      </p>
                      <p className="text-xs text-signal-muted">
                        Chief Recruiter
                      </p>
                    </div>
                  </div>
                )}
                {blogPost.meta.publishDate && (
                  <span className="text-sm text-signal-muted">•</span>
                )}
                {blogPost.meta.publishDate && (
                  <p className="text-sm text-signal-muted">
                    {formatDate(blogPost.meta.publishDate, i18n.language)}
                  </p>
                )}
                <span className="text-sm text-signal-muted">•</span>
                <p className="text-sm text-signal-muted">
                  {readingTime} {t("blog.minRead")}
                </p>
              </div>
              <ShareButtons title={blogPost.heroSection.headline} />
            </div>
            {pillar && (
              <p className="text-sm text-signal-muted mb-2">
                {t("blog.partOf")}{" "}
                <Link
                  href={`/blog/${pillar.slug}`}
                  className="text-signal-deep hover:text-signal-ink underline underline-offset-2"
                >
                  {pillar.title}
                </Link>
              </p>
            )}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-signal-ink mb-4">
              {blogPost.heroSection.headline}
            </h1>
            <p className="text-xl md:text-2xl text-signal-muted mb-6">
              {blogPost.heroSection.subheadline}
            </p>
          </section>

          {/* Intro Section */}
          <section className="mb-12">
            <p className="text-lg md:text-xl text-signal-muted leading-relaxed whitespace-pre-line">
              <RichText text={blogPost.intro.text} />
            </p>
          </section>

          {/* Subtle CTA - Bereit zum Ausprobieren? */}
          <section className="mb-8 glass-inset rounded-glassInner bg-signal-accent/[0.06] p-4 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-signal-muted">
              <span className="font-medium text-signal-deep">
                {blogPost.cta?.subtleCta?.headline ?? t("blog.cta.ready")}
              </span>{" "}
              {blogPost.cta?.subtleCta?.description ?? t("blog.cta.tryFree")}
            </p>
            <Link
              href={blogPost.cta?.subtleCta?.buttonLink ?? "/register"}
              className="btn-glass-primary gap-1 px-4 py-2 text-sm"
            >
              {blogPost.cta?.subtleCta?.buttonLabel ?? t("blog.cta.startNow")}
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
          </section>

          {/* Table of Contents */}
          {tocItems.length > 1 && (
            <nav className="mb-12 glass-inset rounded-glassInner p-6">
              <h2 className="text-xl font-semibold text-signal-ink mb-4">
                {t("blog.tableOfContents")}
              </h2>
              <ul className="space-y-2 list-disc pl-5">
                {tocItems.map(item => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="text-signal-deep hover:text-signal-ink"
                    >
                      <RichText text={item.title} stripLinks />
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {/* Content Blocks */}
          <div className="space-y-12">
            {blogPost.contentBlocks.map((block, index) => {
              const anchorId = anchorIds[index] || undefined;
              return (
                <Fragment key={index}>
                  <ContentBlockRenderer
                    block={block}
                    anchorId={anchorId}
                    tables={blogPost.tables}
                  />
                  {index === 5 && (
                    <section className="rounded-md">
                      <blockquote className="border-l-4 border-signal-accent/30 pl-6 py-4 text-signal-muted italic">
                        <p className="mb-3">
                          {blogPost.cta?.tipBox?.label ?? t("blog.cta.tip")}:{" "}
                          {blogPost.cta?.tipBox?.text ?? t("blog.cta.tipText")}
                        </p>
                        <p>
                          <Link
                            href={blogPost.cta?.tipBox?.linkUrl ?? "/register"}
                            className="text-signal-deep hover:text-signal-ink underline underline-offset-2"
                          >
                            {blogPost.cta?.tipBox?.linkText ??
                              t("blog.cta.socialBoost")}{" "}
                            →
                          </Link>
                        </p>
                      </blockquote>
                    </section>
                  )}
                  {firstCtaIndex !== -1 && index === firstCtaIndex - 1 && (
                    <div className="mt-6">
                      <HowHeyFinnWorks
                        showCta
                        showVideos
                        title={blogPost.cta?.howItWorks?.title}
                        subtitle={blogPost.cta?.howItWorks?.subtitle}
                      />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
          {firstCtaIndex === -1 && (
            <div className="mt-12">
              <HowHeyFinnWorks
                showCta
                showVideos
                title={blogPost.cta?.howItWorks?.title}
                subtitle={blogPost.cta?.howItWorks?.subtitle}
              />
            </div>
          )}

          {/* Internal Links */}
          {blogPost.internalLinks && blogPost.internalLinks.length > 0 && (
            <InternalLinksSection links={blogPost.internalLinks} />
          )}
        </div>
      </article>
      <Footer />
    </div>
  );
}

function InternalLinksSection({
  links,
}: {
  links: NonNullable<BlogPost["internalLinks"]>;
}) {
  const { t } = useTranslation();

  return (
    <section className="mt-16 pt-8 border-t border-signal-ink/10">
      <h2 className="text-2xl font-semibold text-signal-ink mb-6">
        {t("blog.relatedArticles")}
      </h2>
      {Array.isArray(links) && links.some(l => "title" in l) ? (
        <ul className="space-y-5">
          {links.map((raw, index) => {
            if (!("title" in raw)) return null;
            const rich = raw as {
              title: string;
              description?: string;
              slug?: string;
              url?: string;
            };
            const href = rich.url || rich.slug || "#";
            return (
              <li key={index} className="group">
                <Link href={href} className="block">
                  <h3 className="text-xl font-semibold text-signal-deep group-hover:text-signal-ink transition-colors">
                    {rich.title}
                  </h3>
                  {rich.description && (
                    <p className="text-signal-muted mt-1">{rich.description}</p>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <ul className="space-y-3">
          {links.map((raw, index) => {
            if (!("label" in raw) || !("url" in raw)) return null;
            const simple = raw as { label: string; url: string };
            return (
              <li key={index}>
                <Link
                  href={simple.url}
                  className="text-signal-deep hover:text-signal-ink font-medium text-lg transition-colors"
                >
                  → {simple.label}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function BlogTable({
  table,
}: {
  table: { caption?: string; headers: string[]; rows: string[][] };
}) {
  return (
    <div className="my-6 overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full text-left text-sm">
        {table.caption && (
          <caption className="px-4 py-2 text-xs text-signal-muted text-left bg-gray-50 border-b border-gray-200">
            {table.caption}
          </caption>
        )}
        <thead>
          <tr className="bg-gray-50">
            {table.headers.map((header, i) => (
              <th
                key={i}
                className="px-4 py-3 font-semibold text-signal-ink border-b border-gray-200 whitespace-nowrap"
              >
                <RichText text={header} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {table.rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-gray-50 transition-colors">
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="px-4 py-2.5 text-signal-muted whitespace-nowrap"
                >
                  <RichText text={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TextSectionBody({
  body,
  tables,
}: {
  body: string;
  tables?: BlogPost["tables"];
}) {
  if (!tables) {
    return (
      <p className="text-lg text-signal-muted leading-relaxed whitespace-pre-line">
        <RichText text={body} />
      </p>
    );
  }

  // Split body on <table:id> references
  const parts = body.split(/(<table:[a-zA-Z0-9_]+>)/);
  if (parts.length === 1) {
    return (
      <p className="text-lg text-signal-muted leading-relaxed whitespace-pre-line">
        <RichText text={body} />
      </p>
    );
  }

  return (
    <div className="text-lg text-signal-muted leading-relaxed">
      {parts.map((part, i) => {
        const tableMatch = part.match(/^<table:([a-zA-Z0-9_]+)>$/);
        if (tableMatch) {
          const tableId = tableMatch[1];
          const table = tables[tableId];
          if (table) {
            return <BlogTable key={i} table={table} />;
          }
          return null;
        }
        if (part.trim()) {
          return (
            <p key={i} className="whitespace-pre-line">
              <RichText text={part} />
            </p>
          );
        }
        return null;
      })}
    </div>
  );
}

function ContentBlockRenderer({
  block,
  anchorId,
  tables,
}: {
  block: BlogPost["contentBlocks"][number];
  anchorId?: string;
  tables?: BlogPost["tables"];
}) {
  const { t } = useTranslation();
  switch (block.type) {
    case "textSection":
      return (
        <section
          id={anchorId}
          className={anchorId ? "scroll-mt-28" : undefined}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-signal-ink mb-4">
            <RichText text={block.title} />
          </h2>
          <TextSectionBody body={block.body} tables={tables} />
        </section>
      );

    case "bulletList":
      return (
        <section
          id={anchorId}
          className={anchorId ? "scroll-mt-28" : undefined}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-signal-ink mb-6">
            <RichText text={block.title} />
          </h2>
          <ul className="space-y-4">
            {block.items.map((item, index) => (
              <li key={index} className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-signal-accent rounded-full flex items-center justify-center mr-4 mt-1">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>
                <span className="text-lg text-signal-muted leading-relaxed">
                  <RichText text={item} />
                </span>
              </li>
            ))}
          </ul>
        </section>
      );

    case "comparisonSection":
      return (
        <section
          id={anchorId}
          className={anchorId ? "scroll-mt-28" : undefined}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-signal-ink mb-8 text-center">
            <RichText text={block.title} />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Traditional/Old way */}
            <div className="glass-inset rounded-glassInner p-6 relative overflow-hidden">
              <h3 className="text-xl font-bold text-signal-ink mb-4 relative">
                <RichText text={block.left.label} />
              </h3>
              <ul className="space-y-3 relative">
                {block.left.points.map((point, index) => (
                  <li key={index} className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-0.5">
                      <svg
                        className="w-4 h-4 text-amber-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                    <span className="text-base text-signal-muted">
                      <RichText text={point} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Column - heyFinn way */}
            <div className="glass-inset rounded-glassInner bg-signal-accent/[0.08] p-6 relative overflow-hidden ring-1 ring-signal-accent/20">
              <h3 className="text-xl font-bold text-signal-deep mb-4 relative">
                <RichText text={block.right.label} />
              </h3>
              <ul className="space-y-3 relative">
                {block.right.points.map((point, index) => (
                  <li key={index} className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    <span className="text-base text-signal-muted">
                      <RichText text={point} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      );

    case "quoteBox":
      return (
        <section className="rounded-glassInner bg-signal-accent p-8 md:p-12 text-white shadow-glass">
          <blockquote className="text-xl md:text-2xl font-medium italic leading-relaxed">
            <RichText text={block.text} />
          </blockquote>
        </section>
      );

    case "ctaSection":
      return (
        <section
          // The primary CTA is a navy panel with light copy: dark glass tokens
          data-theme={block.style === "primary" ? "dark" : undefined}
          className={`relative rounded-glassInner p-8 md:p-12 text-center overflow-hidden ${
            block.style === "primary"
              ? "glass-flat bg-signal-ink/95 text-white"
              : "glass-inset"
          }`}
        >
          <div className="relative z-10">
            <h2
              className={`text-3xl md:text-4xl font-bold mb-6 ${
                block.style === "primary" ? "text-white" : "text-signal-ink"
              }`}
            >
              {block.headline || t("blog.cta.ready")}
            </h2>
            <p
              className={`text-lg mb-6 ${block.style === "primary" ? "text-gray-300" : "text-signal-muted"}`}
            >
              {block.description || t("blog.cta.tryFree")}
            </p>
            <Link
              href={block.buttonLink}
              className="btn-glass-primary gap-2 px-8 py-4 text-lg"
            >
              {block.buttonLabel || t("blog.cta.startNow")}
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
          </div>
        </section>
      );

    default:
      return null;
  }
}
